import React from "react";
import { Link } from "gatsby";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Fatigue and energy",
		"A carer can pace the day around energy levels, take on practical tasks and help protect time for the activities and relationships that matter most.",
	],
	[
		"02",
		"Mobility and transfers",
		"Support with walking, repositioning and transfers can follow guidance from physiotherapists and occupational therapists, using agreed equipment safely.",
	],
	[
		"03",
		"Personal care",
		"Discreet help with washing, dressing, continence and toileting can adapt to changing strength, coordination and how the person feels that day.",
	],
	[
		"04",
		"Medication and monitoring",
		"Prompts or safe administration can follow the agreed care plan, with new or changing symptoms recorded and shared through the right channels.",
	],
	[
		"05",
		"Therapy and routines",
		"A carer can encourage exercises and strategies recommended by the person's clinical team, without taking over what they can do independently.",
	],
	[
		"06",
		"Thinking and wellbeing",
		"Calm prompts, familiar routines and companionship can help with concentration, confidence and the emotional impact of living with an unpredictable condition.",
	],
];

const changesToShare = [
	"New symptoms or a clear worsening that lasts longer than usual",
	"Changes to vision, sensation, balance or coordination",
	"More difficulty walking, transferring or managing personal care",
	"Fatigue that is having a greater effect on everyday life",
	"Changes to bladder, bowel, swallowing or eating routines",
	"Pain, stiffness or spasms that are new or harder to manage",
];

const msTypes = [
	[
		"Relapsing remitting MS",
		"Symptoms flare up during a relapse and then improve or settle during remission. Around 85% of people with MS are initially diagnosed with this type.",
	],
	[
		"Secondary progressive MS",
		"Some people with relapsing remitting MS later experience a more gradual worsening of symptoms, with or without continued relapses.",
	],
	[
		"Primary progressive MS",
		"Symptoms gradually worsen from the beginning, usually without distinct periods of relapse and remission. The pattern and pace still vary from person to person.",
	],
];

const homePrinciples = [
	[
		"Plan around energy",
		"Keep essentials within easy reach and arrange demanding tasks for the times of day when the person usually has more energy.",
	],
	[
		"Create clear routes",
		"Reduce trip hazards, improve lighting and make enough space for any walking aid, wheelchair or transfer equipment.",
	],
	[
		"Keep the environment comfortable",
		"Agree practical ways to manage temperature, rest and hydration, as heat or tiredness can make some symptoms feel more noticeable.",
	],
	[
		"Review rather than assume",
		"A Care Manager can revisit risks, equipment and routines as symptoms, treatment or independence change.",
	],
];

const faqs = [
	[
		"Can live-in care adapt when MS symptoms vary?",
		"Yes. The care plan can include different approaches for lower- and higher-energy days, while the Care Manager reviews any longer-term changes with the person and family.",
	],
	[
		"Can a carer help during an MS relapse?",
		"A carer can provide additional practical and personal support and help record changes. New or worsening neurological symptoms should be discussed promptly with the person's MS nurse, GP or clinical team.",
	],
	[
		"Can a live-in carer support physiotherapy exercises?",
		"A carer can encourage exercises and movement routines prescribed by a physiotherapist or other qualified professional. They do not design or change a clinical rehabilitation programme.",
	],
	[
		"Can care support bladder or bowel needs?",
		"Yes. Support can include discreet help with toileting, continence routines and prescribed medication, following the care plan and advice from relevant healthcare professionals.",
	],
	[
		"Does live-in care replace an MS nurse?",
		"No. Live-in care provides day-to-day support at home and works alongside neurologists, MS nurses, GPs, therapists and other clinicians. It does not replace medical or nursing care.",
	],
];

export default function MultipleSclerosisCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Multiple Sclerosis Live-in Care at Home | Edyn Care"
				description="Personalised live-in MS care supporting independence, changing symptoms and everyday life at home, managed by Edyn's experienced care team."
				path="/condition/multiple-sclerosis/"
				image="https://www.edyn.care/assets/marketing-site/images/condition_ms_splash.jpeg"
				service={{
					name: "Multiple sclerosis live-in care",
					serviceType: "Multiple sclerosis care at home",
				}}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{
						name: "Multiple sclerosis care",
						path: "/condition/multiple-sclerosis/",
					},
				]}
			/>
			<Header />

			<main
				id="main-content"
				className="edyn-dementia edyn-dementia--textured edyn-ms"
			>
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">
									Multiple sclerosis care at home
								</p>
								<h1>Plan for today. Adapt for tomorrow.</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One-to-one support can flex around changing symptoms while
									helping someone with MS protect their independence at home.
								</p>
								<ul>
									<li>Care shaped around the person, not a fixed routine</li>
									<li>A professional carer in the home</li>
									<li>Ongoing oversight from a Care Manager</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a className="button button--text-light" href="#ms-support">
										Explore support <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual">
							<img
								src={`${imagePath}/condition_ms_splash.jpeg`}
								alt="A woman with a walking stick relaxing in her garden"
								width="1920"
								height="1280"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Flexible support, every day.</strong>
									Independence remains the priority.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Edyn care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One-to-one</strong>
							<span>support in the home</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by an Edyn care team</span>
						</p>
						<p>
							<strong>CQC registered</strong>
							<span>care across England</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">Life with MS</p>
						<div>
							<h2>
								Good care responds to the day without defining the person.
							</h2>
							<div className="dementia-person__copy">
								<p>
									MS affects everyone differently. Symptoms can change over
									time, come and go, or vary from one day to the next. That
									uncertainty can make ordinary routines harder to plan.
								</p>
								<p>
									The right support offers consistency without rigidity: someone
									who understands how the person's MS affects them and can step
									forward or back as each day requires.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="ms-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Practical care that adjusts as symptoms change.</h2>
							</div>
							<p>
								Care follows the agreed plan and works alongside advice from MS
								nurses, neurologists, therapists, GPs and other clinicians.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">Noticing change</p>
							<h2>Familiar support can make patterns easier to see.</h2>
							<p>
								A consistent carer can help keep a simple record of new or
								changing symptoms. That information can be useful when the
								person speaks with their MS nurse or wider clinical team.
							</p>
							<a
								className="dementia-source"
								href="https://www.nhs.uk/conditions/multiple-sclerosis/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read NHS multiple sclerosis guidance{" "}
								<span aria-hidden="true">&#8599;</span>
							</a>
						</div>
						<ul className="dementia-understanding__signs">
							{changesToShare.map((change, index) => (
								<li key={change}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{change}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/we-care-image-2.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The wider picture</p>
								<h2>Multiple sclerosis in the UK</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>Over 150,000</strong>
								<span>people in the UK live with MS</span>
							</p>
							<p>
								<strong>Over 7,000</strong>
								<span>people are newly diagnosed each year</span>
							</p>
							<p>
								<strong>About 1 in 400</strong>
								<span>people in the UK live with MS</span>
							</p>
							<p>
								<strong>Around 85%</strong>
								<span>are initially diagnosed with relapsing remitting MS</span>
							</p>
							<p>
								<strong>2.5 times as many</strong>
								<span>women as men are affected by MS</span>
							</p>
							<p>
								<strong>No two experiences</strong>
								<span>of MS are exactly the same</span>
							</p>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">Different patterns</p>
							<h2>MS is described in three main types.</h2>
							<p>
								These labels describe broad patterns, but each person's
								symptoms, relapses and progression remain individual.
							</p>
						</div>
						<div className="dementia-types__list">
							{msTypes.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual ms-home__visual">
							<img
								src={`${imagePath}/support-teams.png`}
								alt="A supportive home environment adapted around the person"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">Preparing the home</p>
							<h2>Make everyday movement take less energy.</h2>
							<p>
								Before care begins, we assess routines and risks and consider
								practical changes that make the home easier to navigate without
								making it feel clinical.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed multiple sclerosis care
							</p>
							<h2>One carer in the home. A wider team ready to adapt.</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Understand</h3>
								<p>
									A Care Manager learns how MS affects the person, including
									routines, symptoms, risks, equipment and clinical guidance.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match</h3>
								<p>
									We select a professional carer whose experience, manner and
									approach fit the person and their household.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Review</h3>
								<p>
									As symptoms or independence change, the wider Edyn team can
									review risks and update the care plan with the family.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/approach/">
							See how Edyn works
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions families ask</p>
							<h2>
								Clear answers for a condition that can feel unpredictable.
							</h2>
							<p>
								These are some of the questions families often ask when
								considering one-to-one support for someone living with MS.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
